import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Bell, CheckCircle, Package, CurrencyDollar, Info } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  new_order: { icon: Package, color: "text-blue-600" },
  status_update: { icon: Info, color: "text-violet-600" },
  quote_update: { icon: CurrencyDollar, color: "text-amber-600" },
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications);
      // Opening the page counts as reading: clear unread on the server and update the bell badge instantly.
      if (data.unread_count > 0) {
        await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
        window.dispatchEvent(new Event("notifications-read"));
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      window.dispatchEvent(new Event("notifications-read"));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new Event("notifications-read"));
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">NOTIFICATIONS</p>
          <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Notifications</h1>
        </div>
        {notifications.some(n => !n.read) && (
          <button
            onClick={markAllRead}
            className="text-sm text-[#0055FF] hover:text-[#0044CC] font-medium flex items-center gap-1"
            data-testid="mark-all-read-button"
          >
            <CheckCircle size={16} /> Mark all read
          </button>
        )}
      </div>

      <div className="border border-zinc-200" data-testid="notifications-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-500 font-mono">LOADING...</div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {notifications.map(n => {
              const tc = TYPE_CONFIG[n.type] || TYPE_CONFIG.status_update;
              const Icon = tc.icon;
              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`p-4 transition-colors cursor-pointer ${n.read ? "bg-white" : "bg-blue-50/30"}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${tc.color}`}>
                      <Icon size={20} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${n.read ? "text-zinc-600" : "text-zinc-950"}`}>{n.title}</p>
                        {!n.read && <span className="w-2 h-2 bg-[#0055FF] rounded-full flex-shrink-0"></span>}
                      </div>
                      <p className="text-sm text-zinc-500 mt-0.5">{n.message}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-xs text-zinc-400">{new Date(n.created_at).toLocaleString()}</span>
                        {n.order_id && (
                          <Link
                            to={user?.role === "master_admin" ? "/admin/orders" : `/orders/${n.order_id}`}
                            className="text-xs text-[#0055FF] hover:text-[#0044CC] font-medium"
                            onClick={e => e.stopPropagation()}
                            data-testid={`notification-order-link-${n.id}`}
                          >
                            View Order
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
